/**
 * Create Lead Modal
 * Criação rápida de lead (nome, contato e origem).
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { leadsService } from '../../services/leads-service';
import { LeadSource, LEAD_SOURCE_LABELS } from '../../types/leads';

interface CreateLeadModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function FieldError({ id, message }: { id: string; message?: string }) {
  return message ? <p id={id} className="text-xs text-destructive">{message}</p> : null;
}

export const CreateLeadModal: React.FC<CreateLeadModalProps> = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    source: LeadSource.WEBSITE
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData.email && !formData.whatsapp) {
      newErrors.email = 'Informe pelo menos email ou telefone';
      newErrors.whatsapp = 'Informe pelo menos email ou WhatsApp';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      await leadsService.create({
        name: formData.name,
        email: formData.email || undefined,
        whatsapp: formData.whatsapp || undefined,
        source: formData.source
      });
      onSuccess();
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      toast.error('Erro ao criar lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !loading && onClose()}>
      <DialogContent fullScreenOnMobile>
        <DialogHeader>
          <DialogTitle>Novo Lead</DialogTitle>
          <DialogDescription>Área jurídica, valor estimado e notas podem ser preenchidos depois.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lead-name">Nome *</Label>
            <Input
              id="lead-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="João Silva"
              disabled={loading}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'lead-name-error' : undefined}
            />
            <FieldError id="lead-name-error" message={errors.name} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lead-email">Email</Label>
            <Input
              id="lead-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="joao@example.com"
              disabled={loading}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'lead-email-error' : undefined}
            />
            <FieldError id="lead-email-error" message={errors.email} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lead-whatsapp">WhatsApp</Label>
            <Input
              id="lead-whatsapp"
              type="tel"
              value={formData.whatsapp}
              onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
              placeholder="(11) 99999-9999"
              disabled={loading}
              aria-invalid={!!errors.whatsapp}
              aria-describedby={errors.whatsapp ? 'lead-whatsapp-error' : undefined}
            />
            <FieldError id="lead-whatsapp-error" message={errors.whatsapp} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lead-source">Como chegou até nós? *</Label>
            <NativeSelect
              id="lead-source"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value as LeadSource })}
              disabled={loading}
            >
              {Object.values(LeadSource).map((source) => (
                <option key={source} value={source}>{LEAD_SOURCE_LABELS[source]}</option>
              ))}
            </NativeSelect>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="animate-spin" />}
              Criar Lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLeadModal;
